import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { API, http, login } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AvatarBadge } from "@/components/AvatarBadge";
import { ReelComposer } from "@/components/ReelComposer";

const ReelCard = ({ r, canDelete, onLike, onDelete }) => {
  const src = `${API}/reels/${r.id}/media`;
  return (
    <motion.article initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
      className="rounded-[24px] border border-purple-200/20 bg-[#170a24]/85 overflow-hidden" data-testid={`reel-${r.id}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <AvatarBadge avatar={r.author_avatar} size={32} />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-white truncate">{r.author_avatar?.label || r.author_name}</p>
          <p className="text-xs text-purple-100/80">{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</p>
        </div>
        {canDelete && (
          <button onClick={() => onDelete(r.id)} aria-label="Delete post" data-testid={`reel-delete-${r.id}`} className="p-2 text-purple-200 hover:text-red-300">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="bg-black aspect-[4/5]">
        {r.media_type === "video"
          ? <video src={src} controls playsInline loop preload="metadata" className="w-full h-full object-contain" data-testid={`reel-video-${r.id}`} />
          : <img src={src} alt={r.caption || "Reel"} loading="lazy" className="w-full h-full object-cover" data-testid={`reel-image-${r.id}`} />}
      </div>
      <div className="px-4 py-3">
        <button onClick={() => onLike(r.id)} data-testid={`reel-like-${r.id}`} className="inline-flex items-center gap-1.5 text-sm text-purple-50">
          <Heart className={`w-5 h-5 transition-colors ${r.liked ? "fill-pink-400 text-pink-400" : ""}`} />
          <span data-testid={`reel-like-count-${r.id}`}>{r.like_count}</span>
        </button>
        {r.caption && <p className="mt-2 text-sm text-purple-50 whitespace-pre-wrap break-words">{r.caption}</p>}
      </div>
    </motion.article>
  );
};

export default function ReelsPage() {
  const { user } = useAuth();
  const [reels, setReels] = useState(null);
  const [owner, setOwner] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    http.get("/reels").then((r) => setReels(Array.isArray(r.data) ? r.data : [])).catch(() => setReels([]));
  }, [user]);
  useEffect(() => {
    if (!user) { setOwner(false); return; }
    http.get("/auth/is-owner").then((r) => setOwner(Boolean(r.data?.owner))).catch(() => setOwner(false));
  }, [user]);

  const like = async (id) => {
    if (!user) { login(); return; }
    try {
      const { data } = await http.post(`/reels/${id}/like`);
      setReels((rs) => rs.map((r) => (r.id === id ? { ...r, liked: data.liked, like_count: data.like_count } : r)));
    } catch { toast.error("Couldn't like that. Try again."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await http.delete(`/reels/${id}`);
      setReels((rs) => rs.filter((r) => r.id !== id));
    } catch { toast.error("Couldn't delete that post."); }
  };

  return (
    <main className="relative pt-28 pb-24" data-testid="reels-page">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-200">Hollow reels</p>
        <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tight">
          Little moments, <em className="text-purple-300">shared.</em>
        </h1>
        <p className="mt-6 max-w-lg text-purple-50/90 leading-relaxed">Pictures and short videos from everyone in the Hollow. Bracelets, fan art, cosplay and cozy days.</p>
      </section>
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-12 grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-28"><ReelComposer onPosted={(r) => setReels((rs) => [r, ...(rs ?? [])])} /></div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5" data-testid="reels-feed">
          {(reels ?? []).map((r) => (
            <ReelCard key={r.id} r={r} canDelete={owner || r.user_id === user?.user_id} onLike={like} onDelete={remove} />
          ))}
          {reels?.length === 0 && <p className="font-display italic text-2xl text-purple-100 py-10 sm:col-span-2" data-testid="reels-empty">No reels yet. Be the first to share!</p>}
        </div>
      </section>
    </main>
  );
}
